import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, Map as MapIcon, MapPin } from "lucide-react";
import { WorkLocation } from "../../types/works";
import { mountLocationViewer } from "./useGoogleMapPicker";

interface LocationViewProps {
  value?: WorkLocation;
  iconColorClass?: string;
  borderColorClass?: string;
  emptyLabel?: string;
}

/**
 * Read-only counterpart to `LocationField` — shows the location name in the
 * same pill style, with buttons to view it on an inline map or open it in
 * Google Maps. No editing/search.
 */
export const LocationView: React.FC<LocationViewProps> = ({
  value,
  iconColorClass = "text-[#C97A3C]",
  borderColorClass = "border-l-[#C97A3C]",
  emptyLabel,
}) => {
  const { t } = useTranslation("works");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const hasCoords = value?.latitude != null && value?.longitude != null;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !hasCoords || !mapContainerRef.current) return;
    let cancelled = false;
    let handle: { destroy: () => void } | null = null;
    setLoading(true);
    mountLocationViewer(mapContainerRef.current, { lat: value!.latitude!, lng: value!.longitude! }).then((h) => {
      if (cancelled) h.destroy();
      else handle = h;
      setLoading(false);
    });
    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [open, hasCoords, value?.latitude, value?.longitude]);

  if (!value) {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-l-4 border-l-[rgba(11,15,14,0.15)] border-[rgba(11,15,14,0.15)] bg-white pl-3 pr-3 py-2 min-h-[38px]">
        <MapPin size={16} className="shrink-0 text-[#8A9590]" />
        <span className="flex-1 min-w-0 text-[13px] text-[#8A9590] truncate">{emptyLabel}</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-stretch gap-1.5">
        <div
          className={`flex flex-1 min-w-0 items-center gap-2.5 rounded-lg border border-l-4 bg-white pl-3 pr-3 py-2 min-h-[38px] ${borderColorClass} border-[rgba(11,15,14,0.15)]`}
        >
          <MapPin size={16} className={`shrink-0 ${iconColorClass}`} />
          <span className="flex-1 min-w-0 text-[13px] font-medium text-[#2B2F2E] truncate">{value.name}</span>
        </div>
        {hasCoords && (
          <>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              title={t("form.viewOnMap")}
              className={`shrink-0 w-9 flex items-center justify-center rounded-lg border transition-colors ${
                open
                  ? "bg-[#C97A3C] border-[#C97A3C] text-white"
                  : "bg-white border-[rgba(11,15,14,0.15)] text-[#8A9590] hover:text-[#C97A3C]"
              }`}
            >
              <MapIcon size={15} />
            </button>
            <a
              href={`https://www.google.com/maps?q=${value.latitude},${value.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              title={t("form.openInMaps")}
              className="shrink-0 w-9 flex items-center justify-center rounded-lg border bg-white border-[rgba(11,15,14,0.15)] text-[#8A9590] hover:text-[#C97A3C] transition-colors"
            >
              <ExternalLink size={15} />
            </a>
          </>
        )}
      </div>

      {open && hasCoords && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#ECEAE4] rounded-lg shadow-lg overflow-hidden">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
              <Loader2 size={18} className="animate-spin text-[#C97A3C]" />
            </div>
          )}
          <div ref={mapContainerRef} className="h-48 w-full" />
        </div>
      )}
    </div>
  );
};
